import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Heart, Share as ShareIcon, Bookmark, TrendingUp, TrendingDown, ExternalLink, FileText, Newspaper, Link as LinkIcon, Trash2, CornerDownRight, Lock, VolumeX } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { ErrorState as ErrorDisplay } from '../../../components/ui';
import GroupPostComposer from './GroupPostComposer';
import PostReactionButton from './GroupPostButton';
import { renderContentWithMentions } from './mentionRenderUtils';
import useMentionAutocomplete from './useMentionAutocomplete';
import MentionSuggestionDropdown from './MentionSuggestionDropdown';
import { InfoTooltip } from '../../Dashboard/UnifiedDashboard/Tooltip';
import { parseUtcDate } from '../../../utils/parseUtcDate';
import CredibilityChip from '../../../components/Credibility/CredibilityChip';
import UserAvatar from '../../../components/UserAvatar/UserAvatar';
import { resolveMediaUrl } from '../../../utils/resolveMediaUrl';

// ---------------------------------------------------------------------------
// Comment thread — rendered inline when a post's comment section is expanded
// ---------------------------------------------------------------------------

const timeAgo = (iso, t) => {
  if (!iso) return '';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return t('groups.posts.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('groups.posts.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('groups.posts.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('groups.posts.daysAgo', { count: days });
  return new Date(iso).toLocaleDateString();
};

// Groups a flat [{id, parent_comment_id, ...}] list into a real tree, each
// node gaining a `replies` array -- #15: comments used to render as one
// chronological list indented purely by `comment.depth`, so a reply posted
// out of order relative to sibling replies could visually sit under the
// wrong parent. `parent_comment_id` was always correct; it just never
// determined render order/grouping, only indentation.
const buildCommentTree = (flatComments) => {
  const byId = new Map(flatComments.map((c) => [c.id, { ...c, replies: [] }]));
  const roots = [];
  for (const c of byId.values()) {
    const parent = c.parent_comment_id != null ? byId.get(c.parent_comment_id) : null;
    if (parent) {
      parent.replies.push(c);
    } else {
      // Orphaned replies (parent_comment_id set but parent missing/deleted)
      // still need to render somewhere rather than silently vanish.
      roots.push(c);
    }
  }
  const byCreatedAtAsc = (a, b) => new Date(a.created_at) - new Date(b.created_at);
  const sortRecursive = (nodes) => {
    nodes.sort(byCreatedAtAsc);
    nodes.forEach((n) => sortRecursive(n.replies));
  };
  sortRecursive(roots);
  return roots;
};

const CommentRow = ({ comment, currentUser, onReply, onDelete, onLikeToggle }) => {
  const { t } = useTranslation();
  const isAuthor = currentUser && comment.user_id === currentUser.id;
  return (
    <div className="flex items-start gap-3" style={{ marginLeft: Math.min(comment.depth || 0, 4) * 24 }}>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
        {comment.profile_picture ? (
          <img src={resolveMediaUrl(comment.profile_picture)} alt={comment.username} className="w-full h-full rounded-full object-cover" />
        ) : (
          <span className="text-white text-xs font-semibold">{(comment.username || 'U').charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-cream-100 dark:bg-gray-900/50 border border-cream-300 dark:border-gray-700/40 rounded-xl px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-white">{comment.username || t('groups.posts.unknownUser')}</span>
            <CredibilityChip cred={comment.author_credibility} size="xs" showAccuracy={false} />
            <span className="text-xs text-gray-500 dark:text-gray-500">{timeAgo(comment.created_at, t)}</span>
            {comment.is_edited && <span className="text-xs text-gray-400 dark:text-gray-600 italic">{t('groups.posts.edited')}</span>}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap mt-0.5">
            {renderContentWithMentions(comment.content, comment.mentioned_usernames)}
          </p>
        </div>
        <div className="flex items-center gap-4 mt-1 ml-1">
          <button
            onClick={() => onLikeToggle(comment)}
            className={`flex items-center gap-1 text-xs transition-colors ${
              comment.is_liked ? 'text-red-400' : 'text-gray-500 dark:text-gray-500 hover:text-red-400'
            }`}
          >
            <Heart size={12} fill={comment.is_liked ? 'currentColor' : 'none'} />
            {comment.like_count > 0 && <span>{comment.like_count}</span>}
          </button>
          <button
            onClick={() => onReply(comment)}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-500 hover:text-blue-400 transition-colors"
          >
            <CornerDownRight size={12} />
            {t('groups.posts.reply')}
          </button>
          {isAuthor && (
            <button
              onClick={() => onDelete(comment)}
              className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-500 hover:text-red-400 transition-colors"
            >
              <Trash2 size={12} />
              {t('groups.posts.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Recursive: renders a comment, then each of its replies directly beneath
// it (in the tree, not the flat array), matching real thread structure.
const CommentThread = ({ comment, currentUser, onReply, onDelete, onLikeToggle }) => (
  <>
    <CommentRow
      comment={comment}
      currentUser={currentUser}
      onReply={onReply}
      onDelete={onDelete}
      onLikeToggle={onLikeToggle}
    />
    {comment.replies.map((reply) => (
      <CommentThread
        key={reply.id}
        comment={reply}
        currentUser={currentUser}
        onReply={onReply}
        onDelete={onDelete}
        onLikeToggle={onLikeToggle}
      />
    ))}
  </>
);

const CommentComposer = ({ currentUser, replyTo, onCancelReply, onSubmit }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const inputRef = useRef(null);
  const mention = useMentionAutocomplete();

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed, replyTo?.id ?? null);
    setValue('');
    mention.closeSuggestions();
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setValue(text);
    mention.handleTextChange(text, e.target.selectionStart ?? text.length);
  };

  const handleKeyDown = (e) => {
    const result = mention.handleKeyDown(e.key);
    if (result === undefined) return; // not a mention-nav key, let it behave normally (e.g. Enter submits)
    e.preventDefault();
    if (result) {
      const cursor = inputRef.current?.selectionStart ?? value.length;
      const { text, cursorIndex } = mention.applyMention(value, cursor, result);
      setValue(text);
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(cursorIndex, cursorIndex);
        inputRef.current?.focus();
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-start gap-3">
      <UserAvatar userId={currentUser?.id} username={currentUser?.username} className="w-8 h-8" textClass="text-xs" />
      <div className="flex-1">
        {replyTo && (
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1 px-1">
            <span>{t('groups.posts.replyingTo')} <span className="text-gray-600 dark:text-gray-300">{replyTo.username}</span></span>
            <button type="button" onClick={onCancelReply} className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">{t('groups.posts.cancel')}</button>
          </div>
        )}
        <div className="flex items-center gap-2 relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={mention.closeSuggestions}
            placeholder={replyTo ? t('groups.posts.replyPlaceholder') : t('groups.posts.commentPlaceholder')}
            className="flex-1 bg-cream-100 dark:bg-gray-900/50 border border-cream-300 dark:border-gray-700/50 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {mention.isActive && (
            <MentionSuggestionDropdown
              suggestions={mention.suggestions}
              highlightedIndex={mention.highlightedIndex}
              loading={mention.loading}
              onSelect={(user) => {
                const cursor = inputRef.current?.selectionStart ?? value.length;
                const { text, cursorIndex } = mention.applyMention(value, cursor, user);
                setValue(text);
                requestAnimationFrame(() => {
                  inputRef.current?.setSelectionRange(cursorIndex, cursorIndex);
                  inputRef.current?.focus();
                });
              }}
            />
          )}
          <button
            type="submit"
            disabled={!value.trim()}
            className="px-4 py-2 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-xl transition-colors"
          >
            {t('groups.posts.post')}
          </button>
        </div>
      </div>
    </form>
  );
};

const CommentSection = ({ post, currentUser, isMuted = false, mutedUntil, onCreateComment, onDeleteComment, onLikeComment, onUnlikeComment }) => {
  const { t } = useTranslation();
  const [replyTo, setReplyTo] = useState(null);
  const comments = post.comments || [];
  const commentTree = useMemo(() => buildCommentTree(comments), [comments]);

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700/50 space-y-4">
      {comments.length > 0 && (
        <div className="space-y-3">
          {commentTree.map((c) => (
            <CommentThread
              key={c.id}
              comment={c}
              currentUser={currentUser}
              onReply={setReplyTo}
              onDelete={(comment) => onDeleteComment({ postId: post.id, commentId: comment.id })}
              onLikeToggle={(comment) =>
                comment.is_liked
                  ? onUnlikeComment({ postId: post.id, commentId: comment.id })
                  : onLikeComment({ postId: post.id, commentId: comment.id })
              }
            />
          ))}
        </div>
      )}
      {isMuted ? (
        <p className="text-xs text-amber-400 flex items-center gap-1.5">
          <VolumeX size={13} />
          {t('groups.posts.mutedCommentMessage', { untilSuffix: mutedUntil ? t('groups.posts.mutedUntilSuffix', { date: parseUtcDate(mutedUntil).toLocaleString() }) : '' })}
        </p>
      ) : (
        <CommentComposer
          currentUser={currentUser}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSubmit={(content, parentCommentId) => {
            onCreateComment({ postId: post.id, content, parentCommentId });
            setReplyTo(null);
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Evidence badge — renders attached sources below a post
// ---------------------------------------------------------------------------

const EVIDENCE_COLORS = {
  news_article: {
    badge: 'bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
    icon: Newspaper,
  },
  sec_filing: {
    badge: 'bg-violet-100 text-violet-700 border border-violet-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30',
    icon: FileText,
  },
  other: {
    badge: 'bg-gray-500/20 text-gray-600 dark:text-gray-300 border border-gray-400/30 dark:border-gray-500/30',
    icon: LinkIcon,
  },
};

const EvidenceBadge = ({ item }) => {
  const { t } = useTranslation();
  const meta = EVIDENCE_COLORS[item.evidence_type] || EVIDENCE_COLORS.other;
  const Icon = meta.icon;

  return (
    <div className="flex items-start gap-2 px-3 py-2 bg-cream-100 dark:bg-gray-900/40 border border-cream-300 dark:border-gray-700/40 rounded-lg text-xs">
      <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-600 dark:text-gray-400" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${meta.badge}`}>
            {item.evidence_type === 'news_article' ? t('groups.posts.evidenceLabelNews') : item.evidence_type === 'sec_filing' ? t('groups.posts.evidenceLabelSec') : t('groups.posts.evidenceLabelOther')}
          </span>
          <span className="text-gray-900 dark:text-white font-medium truncate">{item.title}</span>
        </div>
        {item.excerpt && (
          <p className="text-gray-600 dark:text-gray-400 line-clamp-1 leading-relaxed">{item.excerpt}</p>
        )}
        {item.relevance_note && (
          <p className="text-gray-500 dark:text-gray-500 italic mt-0.5">"{item.relevance_note}"</p>
        )}
        {item.source_url && item.source_url !== '#' && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-1 transition-colors"
          >
            <span className="truncate max-w-[260px]">{item.source_url}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sentiment pill
// ---------------------------------------------------------------------------

const SentimentPill = ({ sentimentType }) => {
  const { t } = useTranslation();
  if (!sentimentType || sentimentType === 'neutral') return null;
  // 2026-07-25: was `=== 'bullish'` -- the backend only ever produces
  // 'bull'/'bear'/'neutral' (crud_vader_sentiment.py's vocabulary, also
  // used by credibility_service.py and the sort-option ids in
  // Stockers/Posts.jsx), so this literal mismatch made every non-neutral
  // post render as the red "Bearish" pill regardless of real direction.
  const isBull = sentimentType === 'bull';
  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
      isBull
        ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30'
        : 'bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30'
    }`}>
      {isBull ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {isBull ? t('groups.posts.bullish') : t('groups.posts.bearish')}
    </span>
  );
};

// ---------------------------------------------------------------------------
// GroupPosts — main export
// ---------------------------------------------------------------------------

const GroupPosts = ({
  groupId,
  currentUser,
  isMember,
  canCreatePost = true,  // owner/admin/moderator can post; plain members can only comment
  isMuted = false,       // the current user's own mute state in this group
  mutedUntil,            // null/undefined = indefinite mute
  posts = [],
  hasMore = false,
  status = 'idle',
  error,
  onCreatePost,    // async ({ title, content, evidence }) => void  — from GroupDetailPage
  onLoadMore,      // () => void
  onLike,          // ({ postId }) => void  — optional, wired from parent if reactions enabled
  onUnlike,        // ({ postId }) => void
  onBull,          // ({ postId }) => void
  onUnbull,        // ({ postId }) => void
  onBear,          // ({ postId }) => void
  onUnbear,        // ({ postId }) => void
  groupStocks = [], // ✅ tagged stocks — passed into composer for evidence suggestions
  onCreateComment, // ({ postId, content, parentCommentId }) => void
  onDeleteComment, // ({ postId, commentId }) => void
  onLikeComment,   // ({ postId, commentId }) => void
  onUnlikeComment, // ({ postId, commentId }) => void
  onSave,          // ({ groupPostId }) => void
  onUnsave,        // ({ groupPostId }) => void
  savedGroupPostIds = [], // number[] -- ids currently saved by the viewer
}) => {
  const { t } = useTranslation();
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null);
  // Reaction buttons dispatched onLike/onBull/onBear with no in-flight
  // tracking at all -- GroupPostButton (PostReactionButton) already
  // supports an `isLoading` prop, nothing here ever passed it. Keyed by
  // `${postId}-${type}` so reacting to one post/type doesn't disable a
  // different post's (or a different reaction type's) button.
  const [reactingKeys, setReactingKeys] = useState(() => new Set());

  const handleReaction = useCallback(async (key, action) => {
    if (!action || reactingKeys.has(key)) return;
    setReactingKeys((prev) => new Set(prev).add(key));
    try {
      await action();
    } finally {
      setReactingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [reactingKeys]);

  const savedGroupPostIdSet = useMemo(() => new Set(savedGroupPostIds), [savedGroupPostIds]);

  if (status === 'loading' && posts.length === 0) return <LoadingSpinner />;
  if (error) return <ErrorDisplay message={error} onRetry={onLoadMore} />;

  return (
    <div className="space-y-6">

      {/* ── Evidence-aware post composer (members only) ── */}
      {isMember && (
        isMuted ? (
          <div className="text-center py-8 bg-cream-100 dark:bg-gray-800/30 rounded-2xl border border-cream-300 dark:border-gray-700/50">
            <VolumeX className="mx-auto text-amber-400 mb-3" size={32} />
            <p className="text-gray-600 dark:text-gray-400 font-medium">
              {t('groups.posts.mutedPostMessage', { untilSuffix: mutedUntil ? t('groups.posts.mutedUntilSuffix', { date: parseUtcDate(mutedUntil).toLocaleString() }) : '' })}
            </p>
          </div>
        ) : canCreatePost ? (
          <GroupPostComposer
            groupId={groupId}
            currentUser={currentUser}
            onCreatePost={onCreatePost}
            groupStocks={groupStocks}
          />
        ) : (
          <div className="text-center py-8 bg-cream-100 dark:bg-gray-800/30 rounded-2xl border border-cream-300 dark:border-gray-700/50">
            <Lock className="mx-auto text-gray-400 dark:text-gray-500 mb-3" size={32} />
            <p className="text-gray-600 dark:text-gray-400 font-medium">{t('groups.posts.onlyElevatedCanPost')}</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-1">{t('groups.posts.canStillComment')}</p>
          </div>
        )
      )}

      {/* ── Post feed ── */}
      {posts.length === 0 ? (
        <div className="text-center py-12 bg-cream-100 dark:bg-gray-800/30 rounded-2xl border border-cream-300 dark:border-gray-700/50">
          <MessageCircle className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={48} />
          <p className="text-gray-600 dark:text-gray-400">{t('groups.posts.noPostsYet')}</p>
          <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">{t('groups.posts.beFirstToShare')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((post) => (
            <div key={post.id} className="bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-700/50 rounded-2xl p-6">
              <div className="flex items-start space-x-4">

                {/* Avatar */}
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  {post.profile_picture ? (
                    <img
                      src={resolveMediaUrl(post.profile_picture)}
                      alt={post.username}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-semibold">
                      {(post.username || post.author?.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="flex-grow min-w-0">
                  {/* Author + meta */}
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {post.username || post.author?.username || t('groups.posts.unknownUser')}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {new Date(post.created_at).toLocaleDateString()} at{' '}
                        {new Date(post.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {/* Sentiment pill */}
                    <SentimentPill sentimentType={post.sentiment_type} />
                    {/* Author credibility badge -- always rendered (including
                        an explicit Unranked state) rather than silently
                        vanishing for a genuine 0 score or a brand-new author */}
                    <CredibilityChip cred={post.author_credibility} size="sm" showAccuracy={false} />
                  </div>

                  {/* Title + content */}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{post.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed mb-4">
                    {renderContentWithMentions(post.content, post.mentioned_usernames)}
                  </p>

                  {/* Position disclosure -- MAR Art 20 requires this alongside
                      the recommendation itself, not just captured at submission. */}
                  {post.discloses_position && (
                    <div className="flex items-center gap-1.5 mb-4 -mt-2 text-xs text-amber-600 dark:text-amber-300/90">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400" />
                      {t('groups.posts.positionDisclosure')}
                    </div>
                  )}

                  {/* Evidence */}
                  {post.evidence && post.evidence.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <p className="text-xs text-gray-500 dark:text-gray-500 font-medium uppercase tracking-wide">
                        {t('groups.posts.evidenceHeading', { count: post.evidence.length })}
                      </p>
                      {post.evidence.map((ev, idx) => (
                        <EvidenceBadge key={ev.id ?? idx} item={ev} />
                      ))}
                    </div>
                  )}

                  {/* Reactions */}
                  <div className="flex items-center gap-3 flex-wrap pt-4 border-t border-gray-200 dark:border-gray-700/50">
                    {/* Like */}
                    <PostReactionButton
                      type="like"
                      count={post.likes ?? 0}
                      isActive={!!post.is_liked}
                      onClick={() =>
                        handleReaction(
                          `${post.id}-like`,
                          () => (post.is_liked
                            ? onUnlike?.({ postId: post.id })
                            : onLike?.({ postId: post.id }))
                        )
                      }
                      disabled={!onLike && !onUnlike}
                      isLoading={reactingKeys.has(`${post.id}-like`)}
                    />

                    {/* Bull */}
                    <PostReactionButton
                      type="bull"
                      count={post.bull_count ?? 0}
                      isActive={!!post.i_bulled}
                      onClick={() =>
                        handleReaction(
                          `${post.id}-bull`,
                          () => (post.i_bulled
                            ? onUnbull?.({ postId: post.id })
                            : onBull?.({ postId: post.id }))
                        )
                      }
                      disabled={!onBull && !onUnbull}
                      isLoading={reactingKeys.has(`${post.id}-bull`)}
                    />

                    {/* Bear */}
                    <PostReactionButton
                      type="bear"
                      count={post.bear_count ?? 0}
                      isActive={!!post.i_beared}
                      onClick={() =>
                        handleReaction(
                          `${post.id}-bear`,
                          () => (post.i_beared
                            ? onUnbear?.({ postId: post.id })
                            : onBear?.({ postId: post.id }))
                        )
                      }
                      disabled={!onBear && !onUnbear}
                      isLoading={reactingKeys.has(`${post.id}-bear`)}
                    />

                    {/* Self-voting your own post's direction is the biggest
                        single factor in your credibility score -- only worth
                        surfacing here, not on every post's reactions. */}
                    {currentUser && post.user_id === currentUser.id && (
                      <InfoTooltip content={t('groups.posts.selfVoteTooltip')} />
                    )}

                    {/* Comments toggle */}
                    <button
                      onClick={() => setOpenCommentsPostId((cur) => (cur === post.id ? null : post.id))}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                        openCommentsPostId === post.id
                          ? 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/10 border-blue-500/30'
                          : 'text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-blue-700 dark:hover:text-blue-400'
                      }`}
                    >
                      <MessageCircle size={16} />
                      <span>{post.comments?.length ?? 0}</span>
                    </button>

                    {/* Share */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href + `#post-${post.id}`);
                      }}
                      className="flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-green-700 dark:hover:text-green-400 transition-colors"
                    >
                      <ShareIcon size={16} />
                      <span>{t('groups.posts.share')}</span>
                    </button>

                    {/* Save */}
                    {(() => {
                      const isSaved = savedGroupPostIdSet.has(post.id);
                      const saveKey = `${post.id}-save`;
                      return (
                        <button
                          onClick={() => handleReaction(saveKey, () => (isSaved ? onUnsave : onSave)?.({ groupPostId: post.id }))}
                          disabled={(!onSave && !onUnsave) || reactingKeys.has(saveKey)}
                          className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isSaved
                              ? 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-orange-600 dark:border-yellow-500/30'
                              : 'text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-yellow-700 dark:hover:text-yellow-400'
                          }`}
                        >
                          <Bookmark size={16} fill={isSaved ? 'currentColor' : 'none'} />
                          <span>{isSaved ? t('groups.posts.saved') : t('groups.posts.save')}</span>
                        </button>
                      );
                    })()}
                  </div>

                  {openCommentsPostId === post.id && (
                    <CommentSection
                      post={post}
                      currentUser={currentUser}
                      isMuted={isMuted}
                      mutedUntil={mutedUntil}
                      onCreateComment={onCreateComment}
                      onDeleteComment={onDeleteComment}
                      onLikeComment={onLikeComment}
                      onUnlikeComment={onUnlikeComment}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="text-center">
              <button
                onClick={onLoadMore}
                disabled={status === 'loading'}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl disabled:opacity-50"
              >
                {status === 'loading' ? t('groups.posts.loading') : t('groups.posts.loadMorePosts')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupPosts;